'use client'

import Link from 'next/link'
import * as React from 'react'

import { ExternalLink } from '@/components/marketing/fragments/external-link'
import { ThemeSwitcher } from '@/components/marketing/fragments/theme-switcher'
import { FOOTER_LINKS, SOCIAL_LINKS } from '@/components/marketing/marketing-links'
import { Separator } from '@/components/ui/separator'
import { AppInfo } from '@/constants/app-info'
import { useLingui } from '@lingui/react'
import Image from 'next/image'
import { AnimatedGridPattern } from '../ui/animated-grid-pattern'
import Logo from 'public/marketing/logos/group.png'

export function Footer(): React.JSX.Element {
  const { i18n } = useLingui()
  return (
    <footer className='relative overflow-hidden'>
      <AnimatedGridPattern />
      <div className='mx-auto pb-10 max-sm:pt-10 pt-20 px-5 sm:container'>
        <div className='flex w-full flex-col justify-between gap-10 lg:flex-row lg:items-start lg:text-left'>
          <div className='mt-10 space-y-4 xl:mt-0'>
            <Image
              src={Logo}
              width={377}
              height={84}
              className='max-h-16 w-auto'
              alt='Logo Pione'
            />
            <p className='mt-3 text-sm text-muted-foreground'>
              {i18n._(
                'We aim to shape the digital future by applying artificial intelligence to comprehensive technology ecosystems, creating practical and sustainable value for society.'
              )}
            </p>
            <p className='mt-3 text-sm text-muted-foreground'>
              {i18n._(
                'Operational since February 17, 2014, with nearly 12 years of experience in the Blockchain technology sector.'
              )}
            </p>
            <div className='space-y-2 text-sm text-muted-foreground dark:text-muted-foreground'>
              <p>
                <strong>{i18n._('Email:')} </strong>
                <a
                  href='mailto:info@pionegroup.com'
                  className='hover:text-orange-400 transition-colors dark:hover:text-orange-400'
                >
                  info@pionegroup.com
                </a>
              </p>
              <p>
                <strong>{i18n._('Hotline:')} </strong>
                <a
                  href='tel:+842835355888'
                  className='hover:text-orange-400 transition-colors dark:hover:text-orange-400'
                >
                  (+84) 28 3535 5888
                </a>
              </p>
              <p>
                <strong>{i18n._('Support:')} </strong>
                <a
                  href='tel:+84968608974'
                  className='hover:text-orange-400 transition-colors dark:hover:text-orange-400'
                >
                  (+84) 968 608 974
                </a>
              </p>
              <p>
                <strong>{i18n._('Responsible entity:')} </strong>
                {i18n._('Pione Group Joint Stock Company, Tax ID 0318759141.')}
              </p>
              <p>
                <strong>{i18n._('Head office:')} </strong>
                {i18n._(
                  '304/18 Tan Ky Tan Quy Street, Tan Son Nhi Ward, Tan Phu District, Ho Chi Minh City, Vietnam'
                )}
              </p>
            </div>
          </div>
          <div className='grid w-full grid-cols-2 gap-8 md:grid-cols-4'>
            {FOOTER_LINKS.map((group) => (
              <div key={i18n._(group.title)}>
                <h3 className='text-sm font-semibold text-foreground'>{i18n._(group.title)}</h3>
                <ul role='list' className='mt-6 space-y-2'>
                  {group.links.map((link) => (
                    <li key={i18n._(link.name)}>
                      <Link
                        href={link?.href || ''}
                        title={i18n._(link.name)}
                        target={link.external ? '_blank' : undefined}
                        rel={link.external ? 'noopener noreferrer' : undefined}
                        className='relative text-sm text-muted-foreground transition-colors hover:text-foreground'
                      >
                        {i18n._(link.name)}
                        {link.external && (
                          <ExternalLink className='absolute right-[-10px] top-[2px] opacity-80' />
                        )}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
        <p className='text-center mt-20 text-5xl md:text-9xl lg:text-[12rem] xl:text-[13rem] font-bold bg-clip-text text-transparent bg-gradient-to-b from-neutral-50 dark:from-neutral-950 to-neutral-200 dark:to-neutral-800 inset-x-0'>
          {AppInfo.APP_NAME.split(' ').join('')}
        </p>
        <div className='mt-8 border-t pt-8'>
          <div className='flex flex-col items-center justify-between gap-4 sm:flex-row'>
            <p className='text-sm text-muted-foreground'>
              © {new Date().getFullYear()} {AppInfo.APP_NAME}. {i18n._(`All rights reserved.`)}
            </p>
            <div className='flex flex-row items-center gap-4'>
              {SOCIAL_LINKS.map((link) => (
                <Link
                  key={i18n._(link.name)}
                  title={i18n._(link.name)}
                  href={link.href}
                  className='text-muted-foreground transition-colors hover:text-foreground'
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  <span className='sr-only'>{i18n._(link.name)}</span>
                  {link.icon}
                </Link>
              ))}
              <Separator orientation='vertical' className='h-4' />
              <ThemeSwitcher />
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}
